import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { useLocation, useNavigate, useOutletContext } from 'react-router'
import { Badge, Pagination, StatePanel } from '@/components/common'
import { PRODUCT_KEYS } from '@/config/studioProducts'
import { useOrganization } from '@/contexts/OrganizationContext'
import { prepacksApi } from '@/services/api'
import { normalizePrepack, packRecipeId, resolveTemplates } from './prepackCardModel'
import { ALL_COLUMNS, STATUS_FILTERS, nextSort, statusFilterLabel, statusParams } from './prepackLibraryModel'
import { TERMINAL_RUN_STATUSES } from './prepackScheduleModel'
import { UNSCHEDULED_RECIPE } from './prepackSchedulePaths'
import { scheduleQueryKeys } from './prepackScheduleQueries'
import PackListOverlays from './PackListOverlays'
import PrepackListTable from './PrepackListTable'
import { useLastPageGuard, useListPage, usePackList } from './usePackList'

const PAGE_SIZE = 25
// Narrower than the Library's table: the pack and its game, the legs, the odds, the kickoff and the last change.
// The game and legs columns give up a little of their floor so the table fits a schedule page's card on a desktop.
const NARROWER = { event: 'minmax(200px, 1.4fr)', legs: 'minmax(170px, 1.8fr)' }
const COLUMNS = ALL_COLUMNS
  .filter((column) => ['event', 'legs', 'odds', 'expires', 'updated'].includes(column.key))
  .map((column) => (NARROWER[column.key] ? { ...column, width: NARROWER[column.key] } : column))
// Newest updated first (D5): the Updated header shows it as the active sort.
const DEFAULT_SORT = { key: 'updated', direction: 'desc' }
// How far below the viewport a lazy list starts loading: well before the operator scrolls to it.
const LAZY_ROOT_MARGIN = '800px 0px'

const canObserve = () => typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function'

/**
 * A list of packs scoped on the server by the schedule that made them
 * (#1080): one schedule's (`recipe` is its recipe id) on its page, or the
 * packs no live schedule made (`recipe` is UNSCHEDULED_RECIPE) on Schedules
 * → Unscheduled packs. Every status, newest updated first, 25 a page, with
 * the Library's rows, drawer, bulk Publish to surfaces and Delete. A viewer
 * gets the list and a read-only drawer: no checkboxes, no actions.
 *
 * `id` and `className` are the section's; when the location's hash names
 * `id`, the section loads at once and, once its list is in, scrolls into
 * view and takes focus. `heading` titles it, with the pack count beside it.
 * `emptyTitle` / `emptyDescription` say why there are none. `automationId`
 * is the schedule's rule: a queued run of it that finishes reads the list
 * again. `lazy` waits to read the list until the section nears the viewport
 * (without IntersectionObserver it reads at once).
 */
export default function SchedulePacksPanel({ slug, recipe, id, className = '', heading, emptyTitle, emptyDescription, automationId = null, lazy = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { hasProductAccess } = useOrganization()
  const canConfigure = hasProductAccess(PRODUCT_KEYS.PREPACKS, 'configure')
  const canPublish = hasProductAccess(PRODUCT_KEYS.PREPACKS, 'publish')
  // Selecting serves Publish to surfaces and Delete selected; a viewer has neither.
  const selectable = canConfigure || canPublish
  const unscheduled = recipe === UNSCHEDULED_RECIPE
  const regionRef = useRef(null)
  const linked = Boolean(id) && location.hash === `#${id}`

  // A lazy list reads once it nears the viewport, or at once when the link names it.
  const [nearView, setNearView] = useState(() => !lazy || !canObserve())
  const active = nearView || linked
  useEffect(() => {
    if (active) return undefined
    const element = regionRef.current
    if (!element) return undefined
    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setNearView(true)
        observer.disconnect()
      }
    }, { rootMargin: LAZY_ROOT_MARGIN })
    observer.observe(element)
    return () => observer.disconnect()
  }, [active])

  const [statusFilter, setStatusFilter] = useState('all')
  const [sort, setSort] = useState(DEFAULT_SORT)
  // A new organization, schedule, filter or sort starts on the first page.
  const { page, setPage } = useListPage(JSON.stringify([slug, recipe, statusFilter, sort.key, sort.direction]))

  const params = { recipe, ...statusParams(statusFilter), sort: sort.key, direction: sort.direction, limit: PAGE_SIZE, offset: page * PAGE_SIZE }
  const packsQuery = useQuery({
    // Under the organization's library key, so every pack write elsewhere
    // (the Library, Surfaces, the publish dialog) refreshes this list too.
    queryKey: ['prepacks', 'library', slug, 'schedule', recipe, params],
    queryFn: ({ signal }) => prepacksApi.listPrepacks(slug, params, { signal }),
    enabled: Boolean(slug && recipe && active),
    // Keep the previous page on screen while the next one loads, but never another organization's or schedule's.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey?.[2] === slug && previousQuery?.queryKey?.[4] === recipe ? previous : undefined),
    // A banner picture is generated about a minute after a scheduled pack is created (#606): look again until it lands.
    refetchInterval: (query) => (query.state.data?.items?.some((pack) => pack.banner_status === 'painting') ? 15000 : false),
    // A timed run adds packs while the page stays open: read again when the operator comes back to it
    // (the app turns this off by default), and on Refresh.
    refetchOnWindowFocus: true,
  })
  const configQuery = useQuery({
    queryKey: scheduleQueryKeys.configuration(slug),
    queryFn: () => prepacksApi.getConfiguration(slug),
    enabled: Boolean(slug && active),
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['prepacks', 'library', slug] })

  // A queued run of this schedule that finished made packs (or none): read again.
  const runQueue = useOutletContext()
  const finishedRuns = automationId
    ? (runQueue?.entries || [])
      .filter((entry) => entry.automationId === automationId && TERMINAL_RUN_STATUSES.has(entry.status))
      .map((entry) => entry.run?.id || entry.requestId || entry.id)
      .join('|')
    : ''
  const seenRuns = useRef(finishedRuns)
  useEffect(() => {
    if (finishedRuns === seenRuns.current) return
    seenRuns.current = finishedRuns
    if (finishedRuns) queryClient.invalidateQueries({ queryKey: ['prepacks', 'library', slug] })
  }, [finishedRuns, queryClient, slug])

  const packs = useMemo(() => (packsQuery.data?.items || []).map(normalizePrepack), [packsQuery.data?.items])
  // A delete can leave the current page past the end; this steps back to the last page.
  const { total, pageCount } = useLastPageGuard(packsQuery, { page, setPage, pageSize: PAGE_SIZE })
  // The open pack, the selection, the publish dialog, and the lifecycle and delete flows, as the Library has them.
  const list = usePackList({ slug, packs, listQuery: packsQuery, canConfigure, canPublish, invalidate, resetKey: recipe })

  // The deep link (#packs) from the Schedules table and a run's results: once
  // the list is in, scroll there and move focus to the section, once per
  // navigation (never on a plain load, a save or a refetch).
  const listLoaded = packsQuery.isSuccess
  const jumpedFor = useRef(null)
  useEffect(() => {
    if (!linked || !listLoaded || jumpedFor.current === location.key) return
    const element = regionRef.current
    if (!element) return
    jumpedFor.current = location.key
    const reduceMotion = typeof window.matchMedia === 'function' && Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches)
    element.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
    element.focus({ preventScroll: true })
  }, [linked, listLoaded, location.key])

  // An unscheduled pack with a stamp was made by a schedule since deleted:
  // its Edit menu says so (#863). On a schedule's page the pack's schedule is
  // the page, so Edit is one button.
  const deletedSource = unscheduled && list.openPack && packRecipeId(list.openPack)
    ? { id: packRecipeId(list.openPack), name: null, missing: true }
    : null

  const headingId = id ? `${id}-title` : 'schedule-packs-title'
  return (
    <section
      ref={regionRef}
      id={id}
      tabIndex={-1}
      aria-labelledby={headingId}
      className={`min-w-0 focus:outline-none ${className}`}
      data-testid="schedule-packs"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
        <h2 id={headingId} className="flex items-center gap-2 text-base font-semibold text-[var(--wf-color-text)]">
          {heading}
          {packsQuery.isSuccess ? <>{' '}<Badge compact>{total}</Badge></> : null}
        </h2>
        <div className="flex items-center gap-2">
          <select
            aria-label="Filter packs by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)]"
          >
            {STATUS_FILTERS.map((status) => <option key={status} value={status}>{statusFilterLabel(status)}</option>)}
          </select>
          {/* The Library's refresh: a schedule's runs add packs while the page is open (#1080). */}
          <button
            type="button"
            onClick={() => packsQuery.refetch()}
            disabled={!active || packsQuery.isFetching}
            aria-busy={packsQuery.isFetching}
            aria-label="Refresh packs"
            title="Refresh packs"
            className="flex h-8 w-8 items-center justify-center rounded text-[var(--prepacks-muted)] transition-colors hover:bg-[var(--prepacks-raised)] hover:text-[var(--wf-color-text)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${packsQuery.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto px-5 pb-5 pt-3">
        {packsQuery.isError ? (
          <StatePanel state="error" title="Packs could not be loaded" description={packsQuery.error?.response?.data?.error || 'Try loading the packs again.'} onRetry={() => packsQuery.refetch()} />
        ) : !packsQuery.data ? (
          // Before the first answer, a lazy list not yet in view included: never "no packs" before the server said so.
          <StatePanel state="loading" title="Loading packs" description={unscheduled ? 'Reading the packs no schedule made.' : 'Reading the packs this schedule made.'} />
        ) : total === 0 && statusFilter === 'all' ? (
          <StatePanel state="empty" title={emptyTitle} description={emptyDescription} />
        ) : packs.length === 0 ? (
          <StatePanel state="search-empty" title="No packs match" description="Change or clear the status filter." onClear={() => setStatusFilter('all')} />
        ) : (
          <PrepackListTable
            packs={packs}
            columns={COLUMNS}
            sort={sort}
            onSort={(key) => { if (key) setSort((current) => nextSort(current, key)) }}
            selectable={selectable}
            selected={list.selectedPacks}
            onToggle={list.toggle}
            onToggleAll={list.toggleAll}
            onOpen={list.setOpenPack}
            onDelete={(pack) => list.packDelete.requestDelete([pack.id])}
            canDelete={list.canDelete}
            preparingDelete={list.packDelete.preparingDelete}
          />
        )}
        {total > PAGE_SIZE ? (
          <div className="mt-4">
            <Pagination page={page + 1} pageCount={pageCount} onPageChange={(next) => setPage(next - 1)} label={unscheduled ? 'Unscheduled packs pages' : 'Schedule packs pages'} />
          </div>
        ) : null}
      </div>
      <PackListOverlays
        list={list}
        canConfigure={canConfigure}
        canPublish={canPublish}
        templates={resolveTemplates(configQuery.data)}
        scheduleSource={deletedSource}
        onEdit={(pack) => navigate(`/dashboard/prepacks/generate?id=${encodeURIComponent(pack.id)}`)}
        // The menu's schedule item is disabled for a deleted schedule, so this never runs.
        onEditSchedule={deletedSource ? () => {} : undefined}
        regionRef={regionRef}
      />
    </section>
  )
}
